import { Result, Schema } from 'effect';

/**
 * Zod-compatible parse helpers over Effect Schema codecs.
 *
 * Used when a converted module must keep `parse` / `safeParse` for existing
 * consumers while the shapes themselves are `Schema.*` codecs.
 */

export type ParseIssue = {
  readonly message: string;
  readonly path: ReadonlyArray<PropertyKey>;
};

export type ParseErrorLike = {
  readonly issues: ReadonlyArray<ParseIssue>;
  readonly message: string;
};

export type ParseSuccess<A> = { readonly success: true; readonly data: A };
export type ParseFailure = { readonly success: false; readonly error: ParseErrorLike };
export type ParseResult<A> = ParseSuccess<A> | ParseFailure;

function toParseErrorLike(failure: unknown): ParseErrorLike {
  if (
    typeof failure === 'object' &&
    failure !== null &&
    'message' in failure &&
    typeof failure.message === 'string'
  ) {
    return {
      issues: [{ message: failure.message, path: [] }],
      message: failure.message,
    };
  }
  const message = String(failure);
  return { issues: [{ message, path: [] }], message };
}

export type ParseOptions = {
  readonly onExcessProperty?: 'ignore' | 'error' | 'preserve';
};

export const NonEmptyString = Schema.String.check(Schema.isMinLength(1));

export function withParseApi<S extends Schema.Top>(
  schema: S,
  options?: ParseOptions,
): S & {
  parse: (input: unknown) => S['Type'];
  safeParse: (input: unknown) => ParseResult<S['Type']>;
} {
  const parse = Schema.decodeUnknownSync(schema as never, options);
  const decode = Schema.decodeUnknownResult(schema as never, options);
  return Object.assign(schema, {
    parse(input: unknown) {
      return parse(input);
    },
    safeParse(input: unknown): ParseResult<S['Type']> {
      const result = decode(input);
      if (Result.isSuccess(result)) {
        return { success: true, data: result.success };
      }
      return { success: false, error: toParseErrorLike(result.failure) };
    },
  });
}
